'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback, useMemo, useState } from 'react';
import { Check, CircleX, Loader2, Plus } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/comps/primitives/popover';
import { Button } from '@/comps/primitives/button';
import { SearchInputV2 } from '@/comps/search-input';
import { cn } from '@/utils/shad';

// ---------------------------------------------------------------------------
// Color helpers
// ---------------------------------------------------------------------------

const LABEL_COLORS = ['#ef4444',
// red
'#f97316',
// orange
'#eab308',
// yellow
'#22c55e',
// green
'#3b82f6',
// blue
'#8b5cf6',
// purple
'#ec4899',
// pink
'#14b8a6',
// teal
'#6b7280' // gray
];

/** Deterministically derive a stable color from a label name. */
function getLabelColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  return LABEL_COLORS[Math.abs(hash) % LABEL_COLORS.length];
}

// ---------------------------------------------------------------------------
// LabelChip
// ---------------------------------------------------------------------------

interface LabelChipProps {
  name: string;
  /** Show the × remove button. */
  removable?: boolean;
  onRemove?: () => void;
}
export function LabelChip(t0) {
  const $ = _c(15);
  const {
    name,
    removable,
    onRemove
  } = t0;
  const color = getLabelColor(name);
  const t1 = color + "55";
  const t2 = color + "18";
  let t3;
  if ($[0] !== t1 || $[1] !== t2) {
    t3 = {
      borderColor: t1,
      backgroundColor: t2
    };
    $[0] = t1;
    $[1] = t2;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== color) {
    t4 = <div className="size-2 shrink-0 rounded-full" style={{
      backgroundColor: color
    }} />;
    $[3] = color;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== name) {
    t5 = <span className="text-nowrap text-foreground">{name}</span>;
    $[5] = name;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== onRemove || $[8] !== removable) {
    t6 = removable && onRemove && <CircleX size={12} className="cursor-pointer text-muted-foreground" onClick={e => {
      e.preventDefault();
      e.stopPropagation();
      onRemove();
    }} />;
    $[7] = onRemove;
    $[8] = removable;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t3 || $[11] !== t4 || $[12] !== t5 || $[13] !== t6) {
    t7 = <div className="flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium" style={t3}>{t4}{t5}{t6}</div>;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  return t7;
}

// ---------------------------------------------------------------------------
// LabelPickerItem (row inside the popover)
// ---------------------------------------------------------------------------

function LabelPickerItem(t0) {
  const $ = _c(24);
  const {
    name,
    checked,
    onToggle
  } = t0;
  let t1;
  if ($[0] !== name) {
    t1 = getLabelColor(name);
    $[0] = name;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const color = t1;
  let t2;
  if ($[2] !== onToggle) {
    t2 = e => e.key === "Enter" && onToggle();
    $[2] = onToggle;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const t3 = checked && "bg-accent/40";
  let t4;
  if ($[4] !== t3) {
    t4 = cn("flex cursor-pointer items-center gap-2 px-3 py-2", t3);
    $[4] = t3;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = checked ? "border-primary bg-primary" : "border-border";
  let t6;
  if ($[6] !== t5) {
    t6 = cn("flex size-4 shrink-0 items-center justify-center rounded border transition-colors", t5);
    $[6] = t5;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  let t7;
  if ($[8] !== checked) {
    t7 = checked && <Check size={10} className="text-primary-foreground" />;
    $[8] = checked;
    $[9] = t7;
  } else {
    t7 = $[9];
  }
  let t8;
  if ($[10] !== t6 || $[11] !== t7) {
    t8 = <div className={t6}>{t7}</div>;
    $[10] = t6;
    $[11] = t7;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== color) {
    t9 = <div className="size-2.5 shrink-0 rounded-full" style={{
      backgroundColor: color
    }} />;
    $[13] = color;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== name) {
    t10 = <span className="text-sm">{name}</span>;
    $[15] = name;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== onToggle || $[18] !== t10 || $[19] !== t2 || $[20] !== t4 || $[21] !== t8 || $[22] !== t9) {
    t11 = <div role="button" tabIndex={0} onKeyDown={t2} onClick={onToggle} className={t4}>{t8}{t9}{t10}</div>;
    $[17] = onToggle;
    $[18] = t10;
    $[19] = t2;
    $[20] = t4;
    $[21] = t8;
    $[22] = t9;
    $[23] = t11;
  } else {
    t11 = $[23];
  }
  return t11;
}

// ---------------------------------------------------------------------------
// LabelPicker
// ---------------------------------------------------------------------------

export interface LabelPickerProps {
  /** Names of labels currently applied to this interaction/conversation. */
  currentLabels: string[];
  /** All label names available at the project level. */
  availableLabels: string[];
  /**
   * Called when a label is toggled.
   * @param name  the label name
   * @param adding  true = add, false = remove
   */
  onToggleLabel: (name: string, adding: boolean) => void;
  /** Called when the user wants to create a brand-new label. */
  onCreateLabel: (name: string) => void;
  isLoading?: boolean;
  /** Disable all editing (e.g. for non-admin users). */
  disabled?: boolean;
  /**
   * Portal container for the popover — pass `document.querySelector('#warning-sheet')`
   * to avoid z-index clipping inside the sheet.
   */
  container?: Element | null;
  /**
   * Override the trigger button's className.
   * Use this to match the surrounding button group style (e.g. footer CTAs).
   */
  triggerClassName?: string;
  /**
   * When false, the applied-label chips are not rendered outside the popover.
   * The labels are still shown as checked inside the picker.
   * Defaults to true.
   */
  showChips?: boolean;
  /**
   * When true, the popover stays open after selecting or creating a label.
   * Useful for bulk-apply contexts where users may want to add multiple labels.
   * Defaults to false.
   */
  keepOpenOnSelect?: boolean;
}
export function LabelPicker(t0) {
  const $ = _c(35);
  const {
    currentLabels,
    availableLabels,
    onToggleLabel,
    onCreateLabel,
    isLoading,
    disabled,
    container,
    triggerClassName,
    showChips: t1,
    keepOpenOnSelect: t2
  } = t0;
  const showChips = t1 === undefined ? true : t1;
  const keepOpenOnSelect = t2 === undefined ? false : t2;
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const isBusy = Boolean(isLoading || disabled);
  let t3;
  if ($[0] !== availableLabels || $[1] !== search) {
    let t4;
    if ($[3] !== search) {
      t4 = label => label.toLowerCase().includes(search.toLowerCase());
      $[3] = search;
      $[4] = t4;
    } else {
      t4 = $[4];
    }
    t3 = availableLabels.filter(t4).sort(_temp);
    $[0] = availableLabels;
    $[1] = search;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  const filteredLabels = t3;
  let t4;
  if ($[5] !== availableLabels || $[6] !== search) {
    t4 = search.trim().length > 0 && !availableLabels.some(l => l.toLowerCase() === search.trim().toLowerCase());
    $[5] = availableLabels;
    $[6] = search;
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  const canCreateNew = t4;
  let t5;
  if ($[8] !== isBusy || $[9] !== keepOpenOnSelect || $[10] !== onCreateLabel || $[11] !== search) {
    t5 = () => {
      const name = search.trim();
      if (!name || isBusy) {
        return;
      }
      onCreateLabel(name);
      setSearch("");
      if (!keepOpenOnSelect) {
        setOpen(false);
      }
    };
    $[8] = isBusy;
    $[9] = keepOpenOnSelect;
    $[10] = onCreateLabel;
    $[11] = search;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  const handleCreate = t5;
  let t6;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = o => {
      setOpen(o);
      if (!o) {
        setSearch("");
      }
    };
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  const handleOpenChange = t6;
  let t7;
  if ($[14] !== currentLabels || $[15] !== isBusy || $[16] !== onToggleLabel || $[17] !== showChips) {
    t7 = showChips && currentLabels.map(label_0 => <LabelChip key={label_0} name={label_0} removable={!isBusy} onRemove={() => onToggleLabel(label_0, false)} />);
    $[14] = currentLabels;
    $[15] = isBusy;
    $[16] = onToggleLabel;
    $[17] = showChips;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== canCreateNew || $[20] !== container || $[21] !== currentLabels || $[22] !== disabled || $[23] !== filteredLabels || $[24] !== handleCreate || $[25] !== isBusy || $[26] !== keepOpenOnSelect || $[27] !== onToggleLabel || $[28] !== open || $[29] !== search || $[30] !== triggerClassName) {
    t8 = !disabled && <Popover open={open} onOpenChange={handleOpenChange}><PopoverTrigger asChild={true}><Button variant={triggerClassName ? "ghost" : "outline"} size={triggerClassName ? "sm" : undefined} disabled={isBusy} className={triggerClassName ?? "h-auto rounded-full border-dashed px-2 py-0.5 text-xs font-medium text-muted-foreground"}>{isBusy ? <Loader2 size={12} className="animate-spin" /> : <Plus size={12} />}Add label</Button></PopoverTrigger><PopoverContent container={container} forceMount={true} align="start" side="bottom" className="w-60 bg-background p-0"><div className="border-b px-2 py-2"><SearchInputV2 search={search} setSearch={setSearch} placeholder="Type to search or create..." disabled={isBusy} onKeyDown={e => {
            if (e.key === "Enter" && canCreateNew) {
              handleCreate();
            }
          }} /></div>{filteredLabels.length > 0 && <><div className="max-h-55 overflow-y-auto">{filteredLabels.map(label_1 => {
              const isChecked = currentLabels.includes(label_1);
              return <LabelPickerItem key={label_1} name={label_1} checked={isChecked} onToggle={() => {
                if (isBusy) {
                  return;
                }
                onToggleLabel(label_1, !isChecked);
                if (!keepOpenOnSelect) {
                  setOpen(false);
                }
              }} />;
            })}</div></>}{filteredLabels.length === 0 && !canCreateNew && <div className="px-3 py-3 text-sm text-muted-foreground">No labels found</div>}{canCreateNew && <div role="button" tabIndex={0} onKeyDown={e_0 => e_0.key === "Enter" && handleCreate()} onClick={handleCreate} className="flex cursor-pointer items-center gap-2 border-t px-3 py-2"><Plus size={14} className="text-muted-foreground" /><span className="text-sm">Create label: <span className="font-medium">“{search.trim()}”</span></span></div>}</PopoverContent></Popover>;
    $[19] = canCreateNew;
    $[20] = container;
    $[21] = currentLabels;
    $[22] = disabled;
    $[23] = filteredLabels;
    $[24] = handleCreate;
    $[25] = isBusy;
    $[26] = keepOpenOnSelect;
    $[27] = onToggleLabel;
    $[28] = open;
    $[29] = search;
    $[30] = triggerClassName;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  let t9;
  if ($[32] !== t7 || $[33] !== t8) {
    t9 = <div className="flex flex-wrap items-center gap-1.5">{t7}{t8}</div>;
    $[32] = t7;
    $[33] = t8;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  return t9;
}
function _temp(a, b) {
  return a.localeCompare(b);
}